import { Link } from "react-router-dom";
import { Leaf, ArrowRight } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[hsl(var(--brand-cream))] relative overflow-hidden" data-testid="landing-page">
      <div className="absolute inset-0 grain pointer-events-none opacity-40" />
      <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-20">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--brand-sage))] text-white grid place-items-center">
              <Leaf className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display text-2xl leading-none">Nourish</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--brand-muted))] mt-1">
                by Dt. Amisha Kaushal
              </div>
            </div>
          </div>
          <Link
            to="/admin/login"
            className="px-5 py-2.5 rounded-full bg-[hsl(var(--brand-ink))] text-white text-sm hover:bg-[hsl(var(--brand-sage))] transition-colors"
            data-testid="landing-admin-login-btn"
          >
            Dietician Login
          </Link>
        </header>

        <section className="grid md:grid-cols-12 gap-8 md:gap-12 mt-16 md:mt-24 items-center">
          <div className="md:col-span-7">
            <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))] mb-6">
              Private client portal
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[hsl(var(--brand-ink))]">
              Wholesome food, <span className="font-serif-italic text-[hsl(var(--brand-sage))]">personalised.</span>
            </h1>
            <p className="mt-6 text-lg text-[hsl(var(--brand-muted))] max-w-xl leading-relaxed">
              A quiet, intentional space where Amisha's clients receive their weekly diet plans,
              browse recipes, and follow along — no clutter, no noise.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <a
                href="#how"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))] transition-colors"
                data-testid="landing-learn-more-btn"
              >
                How it works <ArrowRight className="w-4 h-4" />
              </a>
              <Link to="/admin/login" className="text-sm underline underline-offset-4 text-[hsl(var(--brand-ink))]" data-testid="landing-login-link">
                I'm the dietician
              </Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-[hsl(var(--brand-border))] relative">
              <img
                src="https://images.unsplash.com/photo-1593170270631-18e2ac1f2bd7?w=900&q=85"
                alt="Fresh bowl"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-5 left-5 right-5 bg-[hsl(var(--brand-cream))]/90 backdrop-blur-sm border border-[hsl(var(--brand-border))] rounded-lg p-4">
                <div className="font-serif-italic text-[hsl(var(--brand-sage))] text-sm">Customised for you</div>
                <div className="font-display text-xl mt-1">Your weekly plan, always a tap away</div>
              </div>
            </div>
          </div>
        </section>

        <section id="how" className="mt-28">
          <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))] mb-6">How it works</div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: "01", title: "Amisha builds your plan", body: "Weekly meals, timings and preparation notes — crafted around your goals." },
              { step: "02", title: "You receive a private link", body: "No app to install. No password. Just open your link and it's waiting." },
              { step: "03", title: "Cook from the recipe book", body: "Every meal links to step-by-step recipes. Download your plan as PDF anytime." },
            ].map((s) => (
              <div key={s.step} className="bg-white border border-[hsl(var(--brand-border))] rounded-2xl p-7 card-lift">
                <div className="font-display text-4xl text-[hsl(var(--brand-sage))]">{s.step}</div>
                <div className="font-display text-xl mt-3">{s.title}</div>
                <p className="text-[hsl(var(--brand-muted))] mt-2">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-28 text-center">
          <div className="font-serif-italic text-2xl text-[hsl(var(--brand-muted))]">
            Tell Amisha what you need. She'll send you a link.
          </div>
        </div>
      </div>
    </div>
  );
}
